import { ApplicationConfig, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';

import { provideRouter, withComponentInputBinding } from '@angular/router';

import { SentryEnvironmentName, provideCore } from '@gde-mit/angular-core';
import { provideDesignSystem } from '@gde-mit/angular-design-system';

import { CoverGenerationQueue } from '@features/kt/activity-browser/services/cover-generation.queue';

import { COVER_GENERATION_QUEUE } from '@shared/models/cover-generation-queue.port';

import { provideKtSentry } from '@shared/sentry/provide-kt-sentry';

import { client as ktApiClient } from '@api';
import { provideHeyApiClient } from '@api';

import { ENVIRONMENT } from '@environments/environment';

import { ConfirmationService } from 'primeng/api';

import { buildInfo } from '../build-info';
import { initializeApp } from '../initialize-app.functions';
import KT_ROUTES from './features/kt/kt.routes';
import { KT_APP_INTERCEPTORS } from './kt-app-interceptors';

/**
 * Creates the Angular application configuration.
 *
 * Must be called after loadAppEnvironment to ensure runtime config values are used.
 *
 * @returns The Angular ApplicationConfig
 */
export function createAppConfig(): ApplicationConfig {
  return {
    providers: [
      provideBrowserGlobalErrorListeners(),
      provideRouter(KT_ROUTES, withComponentInputBinding()),
      provideCore({
        titleStrategy: { appTitle: 'Kreatív tanulás' },
        interceptors: [...KT_APP_INTERCEPTORS],
        integrations: {
          // keycloak: ENVIRONMENT.ENABLE_KEYCLOAK
          //   ? {
          //       config: {
          //         url: ENVIRONMENT.KEYCLOAK.URL,
          //         realm: ENVIRONMENT.KEYCLOAK.REALM,
          //         clientId: ENVIRONMENT.KEYCLOAK.CLIENT_ID,
          //       },
          //       bearerTokenConditions: [
          //         {
          //           urlPattern: new RegExp(ENVIRONMENT.KEYCLOAK.API_URL_PATTERN),
          //         },
          //       ],
          //     }
          //   : undefined,
          // messaging: {
          //   type: ENVIRONMENT.MESSAGING.TYPE,
          //   wsUrl: ENVIRONMENT.MESSAGING.URL,
          //   userChannelPattern: ENVIRONMENT.MESSAGING.USER_CHANNEL_PATTERN,
          //   systemChannel: ENVIRONMENT.MESSAGING.SYSTEM_CHANNEL,
          // },
          googleTagManager: {
            containerId: ENVIRONMENT.GOOGLE_TAG_MANAGER_ID,
          },
        },
      }),
      // spike/mtp-integration: an empty SENTRY.DSN turns Sentry off completely.
      // Sentry rejects the local envelopes with 403, and the default Sentry
      // ErrorHandler hides the errors from the console. A local run therefore
      // supplies an empty DSN and keeps every log in the browser console.
      ...(ENVIRONMENT.SENTRY.DSN
        ? [
            provideKtSentry({
              dsn: ENVIRONMENT.SENTRY.DSN,
              environment: ENVIRONMENT.SENTRY.ENVIRONMENT as SentryEnvironmentName,
              buildInfo,
              enablePiiFiltering:
                ENVIRONMENT.SENTRY.ENVIRONMENT === 'staging' || ENVIRONMENT.SENTRY.ENVIRONMENT === 'production',
              tracesSampleRate: ENVIRONMENT.SENTRY.ENVIRONMENT === 'production' ? 0.2 : 1,
              transportFailureThreshold: 2,
            }),
          ]
        : []),
      provideDesignSystem({
        materialSymbols: {
          weight: 300,
        },
        palette: {
          primary: {
            50: '#f2edff',
            100: '#e5dbff',
            200: '#cbb8ff',
            300: '#b094ff',
            400: '#9671ff',
            500: '#7c4dff',
            600: '#633ecc',
            700: '#5736b3',
            800: '#3e2780',
            900: '#25174d',
            950: '#190f33',
          },
        },
      }),
      provideHeyApiClient(ktApiClient),
      { provide: COVER_GENERATION_QUEUE, useExisting: CoverGenerationQueue },
      ConfirmationService,
      provideAppInitializer(initializeApp),
    ],
  };
}
